import * as React from 'react'
import { Link, useLocation } from 'react-router-dom'

import { Box, Icon, NumberBadge, Text } from '@factorialco/deprecated-design-system'
import * as Collapsible from '@radix-ui/react-collapsible'
import { AnimatePresence, motion } from 'framer-motion'

import { useEffect } from 'modules/core/hooks/useEffect'
import { OverflowContextProvider } from 'modules/core/lib/overflowContext'

import useIncidenceTypeQueryParam from 'modules/bookkeepers/hooks/useIncidenceTypeQueryParam'
import type { ItemsType } from 'modules/bookkeepers/lib/incidenceTypeNavigation'
import { matchActiveNavItem } from 'modules/bookkeepers/lib/incidenceTypeNavigation'

import SubNavItem from './SubNavItem'
import * as styles from './index.vanilla.css'

interface Props {
  item: ItemsType
}

const NavItem = ({ item }: Props) => {
  const { pathname } = useLocation()
  const { incidenceType } = useIncidenceTypeQueryParam()
  const isActive = matchActiveNavItem(pathname, item, incidenceType)
  const subitems = item.subitems
  const [open, setOpen] = React.useState(isActive)
  const ref = React.useRef(null)

  useEffect(() => {
    if (pathname && !isActive) {
      setOpen(false)
    }
  }, [isActive, pathname])

  const handleClick = () => {
    setOpen(true)
  }

  return (
    <Collapsible.Root open={open} onOpenChange={setOpen}>
      <Box
        paddingBottom="s4"
        flexDirection="row"
        width="full"
        justifyContent="spaceBetween"
        background={isActive ? 'grey800' : undefined}
        borderRadius={{ all: isActive ? 'abs006' : undefined }}
        paddingX="s4"
        paddingY="s6"
        gap="s4"
      >
        <Link key={item.key} to={item.path()} onClick={handleClick} className={styles.fullWidth}>
          <Box flexDirection="row" alignItems="center" justifyContent="spaceBetween">
            <Box flexDirection="row" gap="s2" alignItems="center" justifyContent="flexStart">
              <Box>
                <item.icon style={{ width: '26px', height: '26px', color: '#ffffff' }} />
              </Box>
              <Box className={styles.textWidth}>
                <OverflowContextProvider childrenRef={ref} value={item.label}>
                  <Text.Normal size="200" weight="medium" color="grey100" innerRef={ref} ellipsis>
                    {item.label}
                  </Text.Normal>
                </OverflowContextProvider>
              </Box>
            </Box>
            <Box>
              {item.count && item.count > 0 ? (
                <NumberBadge label={item.count} color={isActive ? 'viridian1000' : 'grey800'} />
              ) : null}
            </Box>
          </Box>
        </Link>
        <Box width="s16" justifyContent="center">
          {subitems && (
            <Collapsible.Trigger>
              <Box className={styles.subnavTriggerIconUp}>
                <Icon.Tiny icon="chevron-down" color="grey700"></Icon.Tiny>
              </Box>
            </Collapsible.Trigger>
          )}
        </Box>
      </Box>
      {subitems && (
        <Collapsible.Content forceMount className={styles.subnavWrapper}>
          <AnimatePresence>
            {open && (
              <motion.div
                initial={{ height: 0 }}
                animate={{ height: 'auto' }}
                transition={{ duration: 0.2, type: 'tween' }}
                exit={{ height: 0 }}
              >
                <Box width="full" paddingTop="s8">
                  {subitems.map((item, index) => (
                    <SubNavItem
                      key={index}
                      path={item.path}
                      name={item.name}
                      count={item.count}
                      parentKey={item.key}
                      fullPath={pathname}
                    />
                  ))}
                </Box>
              </motion.div>
            )}
          </AnimatePresence>
        </Collapsible.Content>
      )}
    </Collapsible.Root>
  )
}

export default NavItem
